import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SavedAudience {
  audience_id: string;
  audience_name: string;
  targeting: Record<string, any>;
  approximate_count: string;
  display_name: string;
}

interface WebhookResponse {
  success: boolean;
  total_count: number;
  data: SavedAudience[];
}

export function useCachedSavedAudiences(userId: string | undefined, adAccountId: string | undefined) {
  return useQuery({
    queryKey: ["cached-saved-audiences", userId, adAccountId],
    queryFn: async (): Promise<SavedAudience[]> => {
      // Step 1: Check Supabase cache first
      const { data: cachedData, error: cacheError } = await supabase
        .from("meta_cached_saved_audiences")
        .select("audience_id, audience_name, targeting, approximate_count, display_name")
        .eq("user_id", userId!)
        .eq("ad_account_id", adAccountId!)
        .order("audience_name");

      if (cacheError) {
        console.error("[useCachedSavedAudiences] Cache query error:", cacheError);
        throw cacheError;
      }

      // Step 2: If cache has data, return it
      if (cachedData && cachedData.length > 0) {
        console.log(`[useCachedSavedAudiences] Cache hit: ${cachedData.length} audiences`);
        return cachedData.map((row) => ({
          audience_id: row.audience_id,
          audience_name: row.audience_name,
          targeting: (row.targeting as Record<string, any>) || {},
          approximate_count: row.approximate_count || "",
          display_name: row.display_name || `${row.audience_name} (${row.audience_id})`,
        }));
      }

      // Step 3: Cache miss - fetch via n8n-proxy
      console.log("[useCachedSavedAudiences] Cache miss, fetching via n8n-proxy...");

      const { data: proxyData, error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
        body: {
          action: "GET_SAVED_AUDIENCES",
          payload: { ad_account_id: adAccountId },
        },
      });

      if (proxyError) {
        throw new Error(`Failed to fetch saved audiences: ${proxyError.message}`);
      }

      const webhookResult: WebhookResponse = proxyData;

      if (!webhookResult.success || !webhookResult.data) {
        console.log("[useCachedSavedAudiences] No audiences returned from webhook");
        return [];
      }

      console.log(`[useCachedSavedAudiences] Fetched ${webhookResult.data.length} audiences from Meta`);

      // Step 4: Upsert into Supabase cache
      if (webhookResult.data.length > 0) {
        const rowsToUpsert = webhookResult.data.map((audience) => ({
          user_id: userId!,
          ad_account_id: adAccountId!,
          audience_id: audience.audience_id,
          audience_name: audience.audience_name,
          targeting: audience.targeting || {},
          approximate_count: audience.approximate_count || "",
          display_name: audience.display_name || `${audience.audience_name} (${audience.audience_id})`,
          updated_at: new Date().toISOString(),
        }));

        const { error: upsertError } = await supabase.from("meta_cached_saved_audiences").upsert(rowsToUpsert, {
          onConflict: "user_id,ad_account_id,audience_id",
        });

        if (upsertError) {
          console.error("[useCachedSavedAudiences] Failed to cache audiences:", upsertError);
        } else {
          console.log(`[useCachedSavedAudiences] Cached ${rowsToUpsert.length} audiences`);
        }
      }

      return webhookResult.data;
    },
    enabled: !!userId && !!adAccountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}
